import React, {useState} from 'react';
import PropTypes from 'prop-types';
import ActionConfirmButton from './ActionConfirmButton';
import {useConfirm} from 'material-ui-confirm';
import {useManageConfirmMutation} from '../../../../api/services/profilesService';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import Box from '@mui/material/Box';

const ConfirmationWrapper = (
    {
        children,
        readonly,
        confirmData,
        hideActions = false,
        additionalActions = null,
        // Maria, 18 October 2026: a section's heading, with its confirm button and date on the same line
        heading = null,
        // Maria, 18 October 2026: on Dietary, the confirm button below the section, not beside the heading
        actionsBelow = false
    }) => {
    const [openValidationDialog, setOpenValidationDialog] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const confirm = useConfirm();
    const [manageConfirm] = useManageConfirmMutation();

    const handleSave = async () => {
        if (confirmData.preValidate) {
            const validationResult = confirmData.preValidate();

            if (!validationResult.isValid) {
                setValidationResult(validationResult.error);
                setOpenValidationDialog(true);

                return;
            }
        }

        confirm({
            title: 'Confirmation',
            confirmationText: 'Confirm',
            description: confirmData.confirmMessage,
        })
            .then(async () => {
                if (confirmData.preValidate) {
                    const validationResult = confirmData.preValidate();

                    setValidationResult(validationResult.error);

                    if (!validationResult.isValid) {
                        return;
                    }
                }

                await confirmData.handleSave(confirmData?.type);
                if (confirmData.confirmOnSave) {
                    await manageConfirm({
                        studentId: confirmData.studentId,
                        type: confirmData.type
                    });
                }
                await confirmData?.onConfirmSuccess();
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        if (confirmData.preValidate) {
            const validationResult = confirmData.preValidate();

            if (!validationResult.isValid) {
                setValidationResult(validationResult.error);
                setOpenValidationDialog(true);

                return;
            }
        }

        return confirm({
            title: 'Confirmation',
            confirmationText: 'Confirm',
            description: confirmData.confirmMessage,
        })
            .then(async () => {
                await manageConfirm({
                    studentId: confirmData.studentId,
                    type: confirmData.type
                });
                await confirmData?.onConfirmSuccess();
            })
            .catch(() => {
            });
    };

    // Maria, 17 October 2026: each section of the profile in its own white card, so the sections are clearly apart
    // without large gaps between them.
    const actions = hideActions
        ? null
        : <ActionConfirmButton
            readonly={readonly}
            confirmedAt={confirmData?.confirmedAt}
            confirmedBy={confirmData?.confirmedBy}
            onConfirm={handleConfirm}
            onSave={handleSave}
            enableSaveButton={confirmData.enableSaveButton}
            enableConfirmButton={confirmData.enableConfirmButton}
            confirmButtonText={confirmData.confirmButtonText}
            confirmTooltip={confirmData.confirmTooltip}
            saveConfirms={Boolean(confirmData.confirmOnSave)}
            additionalActions={additionalActions}
        />;

    return (
        <Box
            width="100%"
            sx={{backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, px: {xs: 1.5, sm: 2.5}, py: 1.5, mb: 1.5, boxSizing: 'border-box'}}
        >
            {heading && (
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 2, rowGap: 1, mb: 0.5, '& .action-confirm': {mt: 0}}}>
                    {heading}
                    {!actionsBelow && actions}
                </Box>
            )}
            {/* A section may place the buttons itself, by giving a function as its children (Maria, 18 October
                2026: Cultural puts them in the empty space beside the last question). */}
            {typeof children === 'function' ? children(actions) : children}
            {typeof children !== 'function' && !heading && !actionsBelow && actions}
            {/* Maria, 18 October 2026: on Dietary, the confirm box below the section, on the left */}
            {typeof children !== 'function' && actionsBelow && (
                <Box sx={{'& .action-confirm': {alignItems: {sm: 'flex-start'}}, '& .action-confirm > :first-of-type': {justifyContent: {sm: 'flex-start'}}}}>
                    {actions}
                </Box>
            )}
            <AdmicityDialog
                handleClose={() => setOpenValidationDialog(false)}
                title={validationResult?.reason ?? 'Validation failed'}
                open={openValidationDialog}
                actions={[
                    {label: 'Ok', onClick: () => setOpenValidationDialog(false)},
                ]}
            >
                {validationResult?.description}
            </AdmicityDialog>
        </Box>
    );
};

ConfirmationWrapper.propTypes = {
    readonly: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
    confirmData: PropTypes.object.isRequired,
    hideActions: PropTypes.bool,
    additionalActions: PropTypes.node,
    heading: PropTypes.node,
    actionsBelow: PropTypes.bool,
};

export default ConfirmationWrapper;
